import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// Async thunks
export const submitContactForm = createAsyncThunk(
  "contact/submitContactForm",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post("/contact/submit", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to submit contact form",
      );
    }
  },
);

export const fetchInquiries = createAsyncThunk(
  "contact/fetchInquiries",
  async (
    { page = 1, limit = 20, status = "", priority = "", search = "" } = {},
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(status && { status }),
        ...(priority && { priority }),
        ...(search && { search }),
      });

      const response = await api.get(`/contact?${params}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch inquiries",
      );
    }
  },
);

export const fetchInquiryById = createAsyncThunk(
  "contact/fetchInquiryById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/contact/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch inquiry",
      );
    }
  },
);

export const updateInquiryStatus = createAsyncThunk(
  "contact/updateInquiryStatus",
  async ({ id, status, priority }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/contact/${id}/status`, {
        status,
        priority,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update inquiry status",
      );
    }
  },
);

export const respondToInquiry = createAsyncThunk(
  "contact/respondToInquiry",
  async ({ id, response }, { rejectWithValue }) => {
    try {
      const responseData = await api.post(
        `/contact/${id}/respond`,
        { response },
      );
      return responseData.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send response",
      );
    }
  },
);

export const deleteInquiry = createAsyncThunk(
  "contact/deleteInquiry",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/contact/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete inquiry",
      );
    }
  },
);

export const fetchInquiryAnalytics = createAsyncThunk(
  "contact/fetchInquiryAnalytics",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/contact/analytics");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch analytics",
      );
    }
  },
);

export const addInquiryNote = createAsyncThunk(
  "contact/addInquiryNote",
  async ({ id, content }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/contact/${id}/note`, {
        content,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add note",
      );
    }
  },
);

// Initial state
const initialState = {
  inquiries: [],
  currentInquiry: null,
  analytics: null,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalInquiries: 0,
    hasNext: false,
    hasPrev: false,
  },
  filters: {
    status: "",
    priority: "",
    search: "",
  },
  formSubmitting: false,
  formError: null,
  formSuccess: false,
};

// Slice
const contactSlice = createSlice({
  name: "contact",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.formError = null;
    },
    clearFormState: (state) => {
      state.formSubmitting = false;
      state.formError = null;
      state.formSuccess = false;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentInquiry: (state) => {
      state.currentInquiry = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Submit contact form
      .addCase(submitContactForm.pending, (state) => {
        state.formSubmitting = true;
        state.formError = null;
        state.formSuccess = false;
      })
      .addCase(submitContactForm.fulfilled, (state, action) => {
        state.formSubmitting = false;
        state.formSuccess = true;
      })
      .addCase(submitContactForm.rejected, (state, action) => {
        state.formSubmitting = false;
        state.formError = action.payload;
      })

      // Fetch inquiries
      .addCase(fetchInquiries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInquiries.fulfilled, (state, action) => {
        state.loading = false;
        state.inquiries = action.payload.data.inquiries;
        state.pagination = action.payload.data.pagination;
      })
      .addCase(fetchInquiries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch inquiry by ID
      .addCase(fetchInquiryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInquiryById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentInquiry = action.payload.data.inquiry;
      })
      .addCase(fetchInquiryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update inquiry status
      .addCase(updateInquiryStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInquiryStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.inquiries.findIndex(
          (inq) => inq._id === action.payload.data.inquiry._id,
        );
        if (index !== -1) {
          state.inquiries[index] = action.payload.data.inquiry;
        }
        if (
          state.currentInquiry &&
          state.currentInquiry._id === action.payload.data.inquiry._id
        ) {
          state.currentInquiry = action.payload.data.inquiry;
        }
      })
      .addCase(updateInquiryStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Respond to inquiry
      .addCase(respondToInquiry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(respondToInquiry.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.inquiries.findIndex(
          (inq) => inq._id === action.payload.data.inquiry._id,
        );
        if (index !== -1) {
          state.inquiries[index] = action.payload.data.inquiry;
        }
        if (
          state.currentInquiry &&
          state.currentInquiry._id === action.payload.data.inquiry._id
        ) {
          state.currentInquiry = action.payload.data.inquiry;
        }
      })
      .addCase(respondToInquiry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete inquiry
      .addCase(deleteInquiry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteInquiry.fulfilled, (state, action) => {
        state.loading = false;
        state.inquiries = state.inquiries.filter(
          (inq) => inq._id !== action.payload,
        );
        state.pagination.totalInquiries -= 1;
      })
      .addCase(deleteInquiry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch analytics
      .addCase(fetchInquiryAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInquiryAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.analytics = action.payload.data;
      })
      .addCase(fetchInquiryAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add note
      .addCase(addInquiryNote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addInquiryNote.fulfilled, (state, action) => {
        state.loading = false;
        if (
          state.currentInquiry &&
          state.currentInquiry._id === action.payload.data.inquiry._id
        ) {
          state.currentInquiry = action.payload.data.inquiry;
        }
      })
      .addCase(addInquiryNote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearFormState, setFilters, clearCurrentInquiry } =
  contactSlice.actions;
export default contactSlice.reducer;
